import { useCallback, useEffect, useState } from 'react';
import {
  Paper, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Checkbox, Chip, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Stack, Box,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  listInspectorMembers, addInspectorMembers, removeInspectorMember,
  retryInspectorMember, getInspectorConfig,
} from '../../../services/inspectorApi';
import { listSecurityHubAccounts } from '../../../services/securityhubApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalSectionHeader, PortalErrorState,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { labelForScanType } from './inspectorScanTypes';
import {
  CK, FONT_BODY, sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  statusChipSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

const INSPECTOR_COLOR = '#7D3C98';
const INSPECTOR_COLOR_DARK = '#652F7D';

const inspectorButtonSx = {
  backgroundColor: INSPECTOR_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: INSPECTOR_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const enrollmentHeader = (
  <PortalPageHeader
    icon={<GroupsIcon sx={{ fontSize: 24 }} />}
    title="Enrollment"
    subtitle="Manage which accounts are scanned by Amazon Inspector"
    color={INSPECTOR_COLOR}
  />
);

// The complete per-cell phase vocabulary of InspectorMemberStatus.
//
// "disabled" is labelled "Removed", never "Disabled". It is a tombstone: the cell
// is only written after the account's Inspector session was torn down AND its IAM
// grant detached, so it describes an account that is gone, not one that is present
// but switched off. Rendering it with the raw word has repeatedly been read as the
// latter.
const PHASE_META = {
  pending: { intent: 'neutral', label: 'Pending' },
  granting: { intent: 'info', label: 'Granting access' },
  enabling: { intent: 'info', label: 'Enabling' },
  enabled: { intent: 'success', label: 'Enabled' },
  failed: { intent: 'error', label: 'Failed' },
  removing: { intent: 'warning', label: 'Removing' },
  disabled: { intent: 'neutral', label: 'Removed' },
};

const phaseMeta = (p) => PHASE_META[p] || { intent: 'neutral', label: formatStatusLabel(p) };

// Phases during which the backend is still working on a cell. "granting" belongs
// here: the grant gate assumes a role and waits for IAM propagation in every
// enrolled account, which runs for minutes, and a page that stops polling through
// it renders a frozen bar over a perfectly healthy operation.
const ACTIVE_PHASES = ['pending', 'granting', 'enabling', 'removing'];

// The one chip that summarises an account whose regions disagree — which, in the
// flat peer model, is the normal case rather than an error state. "failed" wins
// because it is the only phase that carries an action.
const summaryPhase = (phases) => {
  if (phases.includes('failed')) return 'failed';
  for (const p of ['removing', 'granting', 'enabling', 'pending']) {
    if (phases.includes(p)) return p;
  }
  if (phases.every((p) => p === 'disabled')) return 'disabled';
  return 'enabled';
};

// InspectorConfigResponse.linkingRegions is a decoded []string, but tolerate a raw
// JSON string so the page never blanks if the DTO shape shifts.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

// Membership and region/scan-type mutations are refused by the backend unless the
// config is settled, so every button that dispatches one has to be gated on the
// same condition or it produces a guaranteed 400.
const configAllowsMemberOps = (status) => status === 'enabled' || status === 'partial_failure';

// One label/value row of the configuration card.
function ConfigRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{ py: 0.5 }}
    >
      <Typography sx={{ minWidth: 150, fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default function InspectorEnrollmentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // showSpinner is false for post-mutation refreshes: swapping the whole page for
  // a loading state after every Add/Remove/Retry throws away the table the user is
  // watching, and the async work has only just been dispatched anyway.
  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      // Config + members decide whether Inspector is set up at all; these must
      // succeed for the page to render an enrollment state.
      const [c, m] = await Promise.all([getInspectorConfig(), listInspectorMembers()]);
      const rows = m?.data || [];
      setConfig(c?.data || null);
      setMembers(rows);
      // A poll can enroll an account that is still ticked in the picker below —
      // most obviously the ones this page just submitted. Re-sending an enrolled
      // account is skipped server-side, and if EVERY tick is already enrolled the
      // whole request is refused with "no new accounts to add", which reads as a
      // failure of an Add that in fact succeeded.
      const live = new Set(rows.filter((r) => r.phase !== 'disabled').map((r) => r.accountId));
      setSelected((prev) => ([...prev].some((id) => live.has(id))
        ? new Set([...prev].filter((id) => !live.has(id)))
        : prev));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
    // Candidate-account list is best-effort: never let it blank the page.
    // No `|| a?.data` fallback: the envelope's data IS an object, so falling back
    // to it would hand an object to the .filter() below instead of reaching `[]`.
    try {
      const a = await listSecurityHubAccounts();
      setAccounts(a?.data?.accounts || []);
    } catch (_) {
      setAccounts([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll while EITHER a cell is mid-flight OR the config is mid-operation. The
  // config half is not redundant: an update claims the config before it touches a
  // single cell, so keying only on cell phases leaves the bar frozen for the whole
  // opening stretch of every operation.
  const operationRunning = config?.status === 'in_progress'
    || members.some((m) => ACTIVE_PHASES.includes(m.phase));

  // Depend on the BOOLEAN, never on `members`. The tick replaces the members array,
  // so an effect keyed on it would tear down and rebuild the interval on every
  // tick — restarting the countdown forever and freezing the progress bar. A
  // boolean only changes when the operation genuinely starts or ends, so the
  // interval survives every tick and the cleanup below is safe to keep.
  useEffect(() => {
    if (!operationRunning) return undefined;
    const id = setInterval(async () => {
      try {
        const [m, c] = await Promise.all([listInspectorMembers(), getInspectorConfig()]);
        setMembers(m?.data || []);
        setConfig(c?.data || null);
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [operationRunning]);

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading enrollment…" color={INSPECTOR_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  // MUST precede the not-configured branch. A rejection from GET /config or GET
  // /members leaves `config` null, so without this a 500 renders as "Inspector is
  // not set up yet" and points the operator at the setup wizard for a capability
  // that is configured and running — while the real message, already captured in
  // `error`, is discarded with the rest of the normal page.
  if (error && !config) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => load()} />
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/inspector')}>
              Go to setup
            </Button>
          }>
            Amazon Inspector is not set up yet for this customer. Complete the setup
            wizard first to enroll accounts.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  // One row per (account, region) collapses to one row per account, because every
  // action the API offers — retry, remove — is account-scoped. The per-region truth
  // is not thrown away: it is rendered as a chip per region inside the row.
  const byAccount = new Map();
  for (const m of members) {
    const prev = byAccount.get(m.accountId) || {
      accountId: m.accountId, cells: [], phases: [], errors: [], retries: 0,
    };
    prev.cells.push(m);
    prev.phases.push(m.phase);
    if (m.errorMessage) prev.errors.push(`${m.region}: ${m.errorMessage}`);
    prev.retries = Math.max(prev.retries, m.retryCount || 0);
    byAccount.set(m.accountId, prev);
  }

  // An account whose every cell is a tombstone is NOT enrolled. It keeps its rows
  // forever (they are the grant-detach ledger the backend needs to clean up a
  // leaked policy), so filtering has to happen here: leaving it in the enrolled
  // table would show a removed, un-granted, non-billing account as a live one.
  // It reappears under "Not enrolled" instead, flagged as previously removed, which
  // is also the path that re-enrolls it.
  const allRows = [...byAccount.values()];
  const enrolledAccounts = allRows.filter((row) => summaryPhase(row.phases) !== 'disabled');
  const removedIds = new Set(
    allRows.filter((row) => summaryPhase(row.phases) === 'disabled').map((r) => r.accountId),
  );
  const enrolledIds = new Set(enrolledAccounts.map((r) => r.accountId));
  const notEnrolled = accounts.filter((a) => !enrolledIds.has(a.accountId));

  // GET /securityhub/accounts returns {accounts:[{accountId, name, status}]} —
  // `name` is the only name field there is. One label helper serves both tables so
  // an account cannot read differently depending on which one it is sitting in.
  const nameById = {};
  accounts.forEach((a) => { if (a.accountId) nameById[a.accountId] = a.name || ''; });
  const acctLabel = (id) => (nameById[id] ? `${nameById[id]} (${id})` : id);

  const canMutate = configAllowsMemberOps(config.status);
  // "failed" is terminal: no fan-out is running, and every mutation gate refuses it
  // until setup is re-run. It must never share the "waiting for the current
  // operation" copy, which promises a completion that will never arrive.
  const setupFailed = config.status === 'failed';
  const failedAccounts = enrolledAccounts.filter((r) => summaryPhase(r.phases) === 'failed');

  const run = async (fn) => {
    setBusy(true);
    let failure = null;
    try {
      await fn();
    } catch (e) {
      failure = e.message;
    }
    // Mutations answer {"status":"in_progress"} and dispatch the real work
    // asynchronously, so the response says nothing about the outcome. Every path
    // re-reads the members list, which is the only source of a row's true state.
    await load(false).catch(() => {});
    // AFTER the reload, never before: a successful load() clears `error`, so a
    // failure set first would be wiped a few milliseconds later and the rejected
    // Add/Remove/Retry would look like it went through.
    setError(failure);
    setBusy(false);
  };

  const onAdd = () => run(async () => {
    await addInspectorMembers([...selected]);
    setSelected(new Set());
  });

  // Close the dialog before dispatching, not after it succeeds: a rejection is
  // reported in the page-level banner, which an open modal would sit on top of.
  const onRemove = (accountId) => {
    setConfirmRemove(null);
    return run(() => removeInspectorMember(accountId));
  };

  // Deliberately one account at a time, with no "retry all". RetryMember flips the
  // config to in_progress before it returns, and the next retry is then refused
  // with "cannot retry while Amazon Inspector is in_progress" — so a loop over the
  // failed accounts would retry the first and report a spurious error for the rest.
  const onRetry = (accountId) => run(() => retryInspectorMember(accountId));

  const linkingRegions = toStringArray(config.linkingRegions);
  const scanTypes = toStringArray(config.resourceTypes);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Configuration"
          subtitle="Amazon Inspector runs standalone in each account — there is no delegated administrator"
          color={INSPECTOR_COLOR}
        />
        <ConfigRow label="Home region" value={config.homeRegion} />
        <ConfigRow label="Linking regions" value={linkingRegions.join(', ')} />
        <ConfigRow label="Scan types" value={scanTypes.map(labelForScanType).join(', ')} />
        <ConfigRow label="Status" value={formatStatusLabel(config.status)} />
      </Paper>
      </motion.div>

      {/* The page still renders in full rather than early-returning like the
          disabled branch: the per-account table below is the only record of how far
          each account got, and it is the diagnostic an operator needs before
          re-running setup. Only the copy has to be honest that nothing is running. */}
      {setupFailed && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/inspector')}>
                Re-run setup
              </Button>
            )}
          >
            Amazon Inspector setup failed{config.errorMessage ? `: ${config.errorMessage}` : '.'}
            {' '}
            Nothing is running and nothing will resume on its own — enrollment
            changes stay unavailable until setup is re-run. The accounts below show
            how far each one got.
          </Alert>
        </motion.div>
      )}

      {/* partial_failure is not a failure banner. Some accounts enrolled and some
          did not, which is the expected shape of a per-account enablement, and the
          only thing the operator needs is the list of the ones to retry. */}
      {config.status === 'partial_failure' && failedAccounts.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {failedAccounts.length === 1
              ? '1 account did not finish enrolling.'
              : `${failedAccounts.length} accounts did not finish enrolling.`}
            {' '}
            Every other account is scanning normally. Use <b>Retry</b> on the rows
            below — a failed account is still enrolled, just not yet scanning.
          </Alert>
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={config.progress}
            label={config.progressLabel || 'Working…'}
            sx={{ mb: 3 }}
          />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<GroupsIcon sx={{ fontSize: 20 }} />}
          title="Enrolled accounts"
          subtitle="Each account runs its own Inspector scan in every linking region"
          color={INSPECTOR_COLOR}
          action={(
            <Chip
              size="small"
              label={`${enrolledAccounts.length} enrolled`}
              sx={statusChipSx('info')}
            />
          )}
        />
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Account</TableCell>
                <TableCell sx={headCellSx}>Regions</TableCell>
                <TableCell sx={headCellSx}>Status</TableCell>
                <TableCell align="right" sx={headCellSx}>Retries</TableCell>
                <TableCell align="right" sx={headCellSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrolledAccounts.map((row, index) => {
                const phase = summaryPhase(row.phases);
                const meta = phaseMeta(phase);
                return (
                  <TableRow key={row.accountId} hover sx={rowSx(index)}>
                    <TableCell sx={bodyCellSx}>{acctLabel(row.accountId)}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {row.cells.map((c) => (
                          <Chip
                            key={c.region}
                            size="small"
                            label={`${c.region} · ${phaseMeta(c.phase).label}`}
                            sx={statusChipSx(phaseMeta(c.phase).intent)}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={meta.label} size="small" sx={statusChipSx(meta.intent)} />
                        {ACTIVE_PHASES.includes(phase) && (
                          <CircularProgress size={14} thickness={5} sx={{ color: INSPECTOR_COLOR }} />
                        )}
                      </Stack>
                      {row.errors.length > 0 && (
                        <Typography
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            fontFamily: FONT_BODY,
                            fontSize: '0.75rem',
                            color: CK.red,
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.errors.join(' • ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {row.retries > 0 ? row.retries : '—'}
                    </TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      {phase === 'failed' && (
                        <Button
                          size="small"
                          color="warning"
                          disabled={busy || !canMutate}
                          onClick={() => onRetry(row.accountId)}
                          sx={{ mr: 1, fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
                        >
                          Retry
                        </Button>
                      )}
                      {phase !== 'removing' && (
                        <Button
                          size="small"
                          color="error"
                          disabled={busy || !canMutate}
                          onClick={() => setConfirmRemove(row.accountId)}
                          sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {enrolledAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<GroupsIcon sx={{ fontSize: 28 }} />}
                      title="No accounts enrolled yet"
                      description="Pick accounts from the list below and add them to start scanning for vulnerabilities."
                      color={INSPECTOR_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<GroupAddIcon sx={{ fontSize: 20 }} />}
          title="Not enrolled"
          subtitle="Linked accounts that Amazon Inspector is not scanning"
          color={INSPECTOR_COLOR}
          action={(
            <Button
              variant="contained"
              disabled={busy || !canMutate || selected.size === 0}
              onClick={onAdd}
              sx={inspectorButtonSx}
            >
              {selected.size === 0 ? 'Add to Inspector' : `Add ${selected.size} to Inspector`}
            </Button>
          )}
        />
        {/* Suppressed when setup failed — the terminal banner above already
            explains it, and this copy would contradict it. What is left is
            in_progress, which the wording is then accurate for. */}
        {!canMutate && !setupFailed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enrollment changes are paused while the current operation finishes.
          </Alert>
        )}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={notEnrolled.some((a) => selected.has(a.accountId)) && !notEnrolled.every((a) => selected.has(a.accountId))}
                    checked={notEnrolled.length > 0 && notEnrolled.every((a) => selected.has(a.accountId))}
                    disabled={notEnrolled.length === 0}
                    onChange={(e) => setSelected(e.target.checked ? new Set(notEnrolled.map((a) => a.accountId)) : new Set())}
                  />
                </TableCell>
                <TableCell sx={headCellSx}>Account</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notEnrolled.map((a, index) => (
                <TableRow key={a.accountId} hover sx={rowSx(index)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected.has(a.accountId)}
                      onChange={(e) => {
                        const n = new Set(selected);
                        if (e.target.checked) n.add(a.accountId);
                        else n.delete(a.accountId);
                        setSelected(n);
                      }}
                    />
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {acctLabel(a.accountId)}
                    {removedIds.has(a.accountId) && (
                      <Chip
                        label="previously removed"
                        size="small"
                        sx={{ ml: 1, ...statusChipSx('neutral') }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {notEnrolled.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: 28 }} />}
                      title="Every linked account is enrolled"
                      description="Nothing left to add — new accounts appear here once they join the organization."
                      color={INSPECTOR_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      <Dialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        {...dialogProps}
        maxWidth="xs"
      >
        <DialogTitle sx={dialogTitleSx}>Remove account from Inspector</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mt: 1 }}>
            Remove <b>{confirmRemove}</b>? Inspector is disabled in that account in
            every linking region, its cached findings are deleted here, and Prism
            detaches the IAM policy it added to the account&apos;s access role.
            Re-adding the account later re-grants it and starts a fresh scan.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setConfirmRemove(null)}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: CK.body }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => onRemove(confirmRemove)}
            disabled={busy}
            sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
